import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2, Plus, Pencil, Trash2, MapPin, RefreshCw, X, Sparkles,
  CheckCircle2, Link2, FileText, CalendarDays, Percent, Globe, Phone, Mail,
  ArrowRight, Settings, Zap, MoreVertical, Camera, Upload, Image
} from 'lucide-react';
import axios from 'axios';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PortalProperties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProp, setEditingProp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pmsConnections, setPmsConnections] = useState([]);
  const [form, setForm] = useState({
    property_name: '', property_code: '', address: '', city: '', state: '', postal_code: '', phone: '', email: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const token = localStorage.getItem('einvoice_token');

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [propRes, pmsRes] = await Promise.all([
        axios.get(`${API_URL}/api/einvoice/portal/properties`, { headers }),
        axios.get(`${API_URL}/api/einvoice/portal/pms/connections`, { headers }).catch(() => ({ data: { connections: [] } }))
      ]);
      setProperties(propRes.data.properties || []);
      setPmsConnections(pmsRes.data.connections || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setEditingProp(null);
    setForm({ property_name: '', property_code: '', address: '', city: '', state: '', postal_code: '', phone: '', email: '' });
    setLogoFile(null);
    setLogoPreview(null);
    setShowForm(true);
  };

  const openEditForm = (prop) => {
    setEditingProp(prop);
    setForm({
      property_name: prop.property_name || '',
      property_code: prop.property_code || '',
      address: prop.address || '',
      city: prop.city || '',
      state: prop.state || '',
      postal_code: prop.postal_code || '',
      phone: prop.phone || '',
      email: prop.email || '',
    });
    setLogoFile(null);
    setLogoPreview(prop.logo_url || null);
    setShowForm(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (propertyId) => {
    if (!logoFile) return null;
    const formData = new FormData();
    formData.append('logo', logoFile);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/einvoice/portal/properties/${propertyId}/logo`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      return data.logo_url;
    } catch (err) {
      console.error('Logo upload failed:', err);
      return null;
    }
  };

  const handleSave = async () => {
    if (!form.property_name.trim()) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let propertyId = editingProp?.id;
      
      if (editingProp) {
        await axios.put(`${API_URL}/api/einvoice/portal/properties/${editingProp.id}`, form, { headers });
        toast.success('Property updated');
      } else {
        const { data } = await axios.post(`${API_URL}/api/einvoice/portal/properties`, form, { headers });
        propertyId = data.property?.id || data.id;
        toast.success('Property created');
      }
      
      // Upload logo if selected
      if (logoFile && propertyId) {
        setUploadingLogo(true);
        await uploadLogo(propertyId);
      }
      
      setShowForm(false);
      setLogoFile(null);
      setLogoPreview(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
      setUploadingLogo(false);
    }
  };

  const handleDelete = async (propId) => {
    if (!window.confirm('Are you sure you want to remove this property? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/einvoice/properties/${propId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property removed');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete property');
    }
  };

  const getPmsStatus = (prop) => {
    if (prop.pms_type && prop.pms_type !== 'manual') {
      const conn = pmsConnections.find(c => c.pms_type === prop.pms_type);
      return { connected: !!conn, type: prop.pms_type };
    }
    return { connected: false, type: null };
  };

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8" data-testid="portal-properties">
        {/* ─── Header ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="properties-title">
                Properties
              </h1>
              <p className="text-slate-500 dark:text-gray-400 text-sm">
                Manage your hotels and accommodation properties
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              size="sm" 
              onClick={loadData} 
              disabled={loading} 
              variant="outline"
              className="border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-zinc-800"
              data-testid="refresh-properties-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button 
              size="sm" 
              onClick={openAddForm} 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25"
              data-testid="add-property-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Property
            </Button>
          </div>
        </div>

        {/* ─── Stats Summary ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Total Properties</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{properties.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-gray-400">PMS Connected</p>
                  <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                    {properties.filter(p => p.pms_type && p.pms_type !== 'manual').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Active</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                    {properties.filter(p => p.status !== 'inactive').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Automation</p>
                  <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                    {pmsConnections.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Add/Edit Form Modal ─── */}
        {showForm && (
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-2xl shadow-slate-300/50 dark:shadow-black/40 overflow-hidden" data-testid="property-form">
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  {editingProp ? <Pencil className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900 dark:text-white">
                    {editingProp ? 'Edit Property' : 'Add New Property'}
                  </CardTitle>
                  <p className="text-xs text-slate-500 dark:text-gray-400">
                    {editingProp ? 'Update property information' : 'Enter your property details'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowForm(false)} 
                className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Property Name *</Label>
                  <Input 
                    value={form.property_name} 
                    onChange={e => setForm({ ...form, property_name: e.target.value })} 
                    placeholder="e.g. Grand Hotel KL" 
                    className="h-11"
                    data-testid="input-property-name" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Property Code</Label>
                  <Input 
                    value={form.property_code} 
                    onChange={e => setForm({ ...form, property_code: e.target.value })} 
                    placeholder="e.g. GHKL001" 
                    className="h-11"
                    data-testid="input-property-code" 
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Address</Label>
                  <Input 
                    value={form.address} 
                    onChange={e => setForm({ ...form, address: e.target.value })} 
                    placeholder="Street address" 
                    className="h-11"
                    data-testid="input-address" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">City</Label>
                  <Input 
                    value={form.city} 
                    onChange={e => setForm({ ...form, city: e.target.value })} 
                    placeholder="e.g. Kuala Lumpur" 
                    className="h-11"
                    data-testid="input-city" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">State</Label>
                  <Input 
                    value={form.state} 
                    onChange={e => setForm({ ...form, state: e.target.value })} 
                    placeholder="e.g. Wilayah Persekutuan" 
                    className="h-11"
                    data-testid="input-state" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Postal Code</Label>
                  <Input 
                    value={form.postal_code} 
                    onChange={e => setForm({ ...form, postal_code: e.target.value })} 
                    placeholder="e.g. 50450" 
                    className="h-11"
                    data-testid="input-postcode" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Phone</Label>
                  <Input 
                    value={form.phone} 
                    onChange={e => setForm({ ...form, phone: e.target.value })} 
                    placeholder="+60 3-1234 5678" 
                    className="h-11"
                    data-testid="input-phone" 
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Email</Label>
                  <Input 
                    value={form.email} 
                    onChange={e => setForm({ ...form, email: e.target.value })} 
                    placeholder="hotel@example.com" 
                    type="email" 
                    className="h-11"
                    data-testid="input-email" 
                  />
                </div>
                
                {/* Logo Upload */}
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-gray-300">Property Logo</Label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="relative w-24 h-24 rounded-2xl border-2 border-dashed border-slate-300 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Image className="w-8 h-8 text-slate-400 dark:text-gray-500 mx-auto mb-1" />
                          <span className="text-xs text-slate-400 dark:text-gray-500">No logo</span>
                        </div>
                      )}
                      {logoPreview && (
                        <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        data-testid="input-logo"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                        className="mb-2"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-slate-500 dark:text-gray-400">
                        PNG, JPG up to 2MB. Recommended: 200x200px
                      </p>
                      {logoFile && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />
                          {logoFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-zinc-800">
                <Button 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                  className="border-slate-200 dark:border-zinc-700"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving || uploadingLogo || !form.property_name.trim()} 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white min-w-[140px]"
                  data-testid="save-property-btn"
                >
                  {(saving || uploadingLogo) ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {uploadingLogo ? 'Uploading...' : editingProp ? 'Update Property' : 'Add Property'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Properties Grid ─── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <RefreshCw className="w-10 h-10 animate-spin text-blue-500 mb-3" />
            <p className="text-sm text-slate-500 dark:text-gray-400">Loading properties...</p>
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="properties-grid">
            {properties.map(prop => {
              const pms = getPmsStatus(prop);
              return (
                <Card 
                  key={prop.id} 
                  className="group relative border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20 hover:shadow-xl hover:shadow-slate-300/50 dark:hover:shadow-black/40 transition-all duration-300 overflow-hidden"
                >
                  {/* Status indicator bar */}
                  <div className={`h-1 ${prop.status === 'inactive' ? 'bg-slate-300 dark:bg-zinc-700' : 'bg-gradient-to-r from-emerald-400 to-teal-500'}`} />
                  
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 overflow-hidden">
                          {prop.logo_url ? (
                            <img src={prop.logo_url} alt={prop.property_name} className="w-full h-full object-cover" />
                          ) : (
                            <Building2 className="w-8 h-8 text-white" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-slate-900 dark:text-white">{prop.property_name}</h3>
                          <p className="text-sm text-slate-400 dark:text-gray-500 font-mono">{prop.property_code || 'No code'}</p>
                        </div>
                      </div>
                      
                      {/* Actions Menu */}
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => openEditForm(prop)} 
                          className="p-2 rounded-lg text-slate-400 dark:text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" 
                          data-testid={`edit-prop-${prop.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(prop.id)} 
                          className="p-2 rounded-lg text-slate-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" 
                          data-testid={`delete-prop-${prop.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Address Section */}
                    {(prop.address || prop.city) && (
                      <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-xl mb-4">
                        <MapPin className="w-4 h-4 text-slate-400 dark:text-gray-500 mt-0.5 shrink-0" />
                        <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">
                          {[prop.address, prop.city, prop.state, prop.postal_code].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {prop.phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
                          <Phone className="w-4 h-4" />
                          <span className="truncate">{prop.phone}</span>
                        </div>
                      )}
                      {prop.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{prop.email}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <Badge className={`flex items-center ${
                        prop.status === 'inactive' 
                          ? 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-gray-400' 
                          : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                      } border-0`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {prop.status || 'Active'}
                      </Badge>
                      {pms.type && (
                        <Badge className="flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-0">
                          <Link2 className="w-3 h-3 mr-1" />
                          {pms.type}
                          {pms.connected && <CheckCircle2 className="w-3 h-3 ml-1 text-emerald-500" />}
                        </Badge>
                      )}
                      <Badge className="flex items-center bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0">
                        <Percent className="w-3 h-3 mr-1" />
                        {prop.tax_rate || 8}% Tax
                      </Badge>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-100 dark:border-zinc-800">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-10 text-sm border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-zinc-800"
                        onClick={() => navigate('/einvoice/portal/integrations')} 
                        data-testid={`connect-pms-${prop.id}`}
                      >
                        <Link2 className="w-4 h-4 mr-2" /> 
                        {pms.type ? 'Manage PMS' : 'Connect PMS'}
                      </Button>
                      <Button 
                        size="sm" 
                        className="h-10 text-sm bg-slate-900 dark:bg-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-gray-100 text-white"
                        onClick={() => navigate('/einvoice/portal/bookings')} 
                        data-testid={`view-bookings-${prop.id}`}
                      >
                        <CalendarDays className="w-4 h-4 mr-2" /> Bookings
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
            <CardContent className="py-20 text-center" data-testid="properties-empty">
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center">
                <Building2 className="w-10 h-10 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No properties yet</h3>
              <p className="text-slate-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Add your first hotel or accommodation property to start generating e-invoices
              </p>
              <Button 
                onClick={openAddForm}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Your First Property
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
